import React, { useMemo } from 'react'
import ReactEChartsCore from './EChartsCore.js'
import * as echarts from 'echarts/core'
import type { ComposeOption } from 'echarts/core'
import { ScatterChart, type ScatterSeriesOption } from 'echarts/charts'
import {
  GridComponent,
  TooltipComponent,
  type GridComponentOption,
  type TooltipComponentOption
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import type { XAXisComponentOption, YAXisComponentOption } from 'echarts'
import { escapeHtml, visTickFormatter } from './Options/vizFormatters.js'
import { CHART_SURFACE } from './Options/visualIdentity.js'

echarts.use([ScatterChart, GridComponent, TooltipComponent, CanvasRenderer])

type EChartsOption = ComposeOption<
  ScatterSeriesOption | GridComponentOption | TooltipComponentOption | XAXisComponentOption | YAXisComponentOption
>

export interface ScatterPoint {
  x: number
  y: number
  name: string
  color?: string
  /** Optional group label shown in the tooltip (e.g. "colour by metadata" value). */
  group?: string
  /** Optional `[label, value]` rows shown in the tooltip, sorted desc by value. */
  details?: Array<[string, number]>
}

interface Props {
  data: ScatterPoint[]
  xName?: string
  yName?: string
  height?: number | `${number}%`
}

/** ECharts scatter, e.g. the PCoA / Bray-Curtis plot. */
export const ScatterChartNext: React.FC<Props> = ({ data, xName, yName, height }) => {
  const option: EChartsOption = useMemo(
    () => ({
      backgroundColor: CHART_SURFACE,
      animation: false,
      grid: { top: 24, bottom: 50, left: 60, right: 24, containLabel: true },
      tooltip: {
        trigger: 'item',
        confine: true,
        formatter: (raw) => {
          const params = Array.isArray(raw) ? raw[0] : raw
          const p = (params?.data as { point?: ScatterPoint })?.point
          if (!p) return ''
          const rows = (p.details ?? [])
            .slice()
            .sort((a, b) => b[1] - a[1])
            .map(
              ([k, v]) =>
                `<div style="display:flex;justify-content:space-between;gap:12px"><span>${escapeHtml(k)}</span>` +
                `<span style="font-variant-numeric:tabular-nums">${escapeHtml(String(visTickFormatter(v) ?? v))}</span></div>`
            )
            .join('')
          const group = p.group ? `<div style="font-size:11px;margin-bottom:4px">Group: ${escapeHtml(p.group)}</div>` : ''
          return `<div style="min-width:200px"><div style="font-weight:700;margin-bottom:4px">${escapeHtml(p.name)}</div>${group}${rows}</div>`
        }
      },
      xAxis: { type: 'value', name: xName ?? 'PC1', nameLocation: 'middle', nameGap: 28, scale: true },
      yAxis: { type: 'value', name: yName ?? 'PC2', nameLocation: 'middle', nameGap: 40, scale: true },
      series: [
        {
          type: 'scatter',
          symbolSize: 12,
          data: data.map((p) => ({
            value: [p.x, p.y],
            point: p,
            itemStyle: p.color ? { color: p.color } : undefined
          })),
          emphasis: { focus: 'self', scale: 1.3 }
        }
      ]
    }),
    [data, xName, yName]
  )

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={option}
      notMerge
      lazyUpdate
      style={{ width: '100%', height: height ?? 400, backgroundColor: CHART_SURFACE }}
    />
  )
}
